import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import isString from 'lodash/isString';

import OptionShape from 'utils/propTypes/option';
import Icon from '../../../Icon';
import { classesShape } from '../../propTypes';
import { getOptionLabel } from '../../utils';
import Tooltip from '../../../Tooltip';
import styles from './Row.module.scss';

/* Default title is for convenience only.
 * If using a DOM node for a custom label, best option is to provide one there yourself.
 * Defaults to `option.labelText` if available, otherwise uses `label` if it's a String.
 */
export const getDefaultTooltip = option => {
  if (option.labelText) {
    return option.labelText;
  }

  const label = getOptionLabel(option);
  return (isString(label) && label) || undefined;
};

const getTooltipContent = option => {
  const {
    disabled,
    disabledTooltipId,
    disabledTooltipContent,
    tooltipContent,
  } = option;

  if (disabled) {
    if (disabledTooltipContent) {
      return disabledTooltipContent;
    }
    if (disabledTooltipId) {
      return disabledTooltipId;
    }
  }
  return tooltipContent;
};

export default function SearchSelectRow({
  checked,
  classes,
  isFocused,
  itemHeight,
  onFocus,
  option,
  selectValue,
  showCheckmarks,
  style,
}) {
  const { disabled } = option;
  const tooltipContent = getTooltipContent(option);

  const handleClick = () => {
    if (!disabled) {
      selectValue(option);
    }
  };

  return (
    <div
      style={{
        height: `${itemHeight}px`,
        ...style,
      }}
      className={classnames(
        styles.selectOptionRow,
        classes.row,
        option.className,
        {
          [styles.isFocused]: isFocused,
          [classes.disabled]: disabled,
        },
      )}
      onClick={handleClick}
      onMouseEnter={onFocus}
      data-testid="menuItem-searchSelect"
      title={() => getDefaultTooltip(option)}
      role="option"
      aria-selected={!!checked}
    >
      <Tooltip
        tooltipContent={tooltipContent}
        active={!!tooltipContent}
        placement="right"
        popperProps={{
          modifiers: {
            preventOverflow: {
              boundariesElement: 'viewport',
            },
          },
        }}
        className={styles.tooltipWrapper}
        classes={option.tooltipClasses}
      >
        <div
          title={getDefaultTooltip(option)}
          className={classnames(styles.content, classes.content, {
            [styles.disabled]: !!disabled,
            [classes.disabledContent]: !!disabled,
          })}
        >
          {showCheckmarks && (
            <Icon
              type="icon-checkmark"
              className={classnames(styles.optionCheckmark, {
                [styles.hidden]: !checked,
              })}
            />
          )}
          <span className={classnames(styles.optionValue, classes.optionValue)}>
            {getOptionLabel(option)}
          </span>
        </div>
      </Tooltip>
    </div>
  );
}

SearchSelectRow.propTypes = {
  /**
   * whether this option is checked
   */
  checked: PropTypes.bool.isRequired,
  /**
   * list of classes to override
   */
  classes: classesShape,
  /**
   * option object containing label and value
   */
  option: OptionShape.isRequired,
  /**
   * focused state
   */
  isFocused: PropTypes.bool.isRequired,
  /**
   * height of row
   */
  itemHeight: PropTypes.number.isRequired,
  /**
   * function to invoke when clicked
   */
  selectValue: PropTypes.func.isRequired,
  /**
   * function to invoke when clicked
   */
  onFocus: PropTypes.func.isRequired,
  /**
   * Show checkmarks in the menu for selected checkmarks
   */
  showCheckmarks: PropTypes.bool.isRequired,
  /**
   * row style
   */
  style: PropTypes.shape({}),
};

SearchSelectRow.defaultProps = {
  classes: {},
  style: {},
};

SearchSelectRow.displayName = 'SearchSelectRow';
